import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger, Dialog, DialogContent
} from '@duck/ui'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { WaitingList } from '@components/WaitingList/WaitingList'
import { ExternalURLs } from '@/types/externalURLs'

export const FaqPage = () => {
    const [isWaitlistOpen, setIsWaitlistOpen] = useState(false)

    return (
        <div className="flex flex-1 w-full justify-center">
            <div className="flex-1 max-w-6xl space-y-12 mt-8">
                {/* Header Section */}
                <div className="text-center space-y-4">
                    <h1 className="text-4xl md:text-4xl font-bold text-foreground">
                        Frequently Asked Questions
                    </h1>
                    <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                        Find answers to common questions about our platform
                    </p>
                </div>

                {/* FAQ Accordion */}
                <div className="bg-card rounded-lg shadow-sm border border-border p-8">
                    <Accordion type="multiple" className="w-full">
                        <AccordionItem value="item-1">
                            <AccordionTrigger>What is XP-lab?</AccordionTrigger>
                            <AccordionContent>
                                <div className="space-y-4">
                                    <p>
                                        <strong>XP-lab</strong> (short for <em>experience lab</em>) is a hands-on practice space for data professionals.
                                    </p>
                                    <p>
                                        We don't teach from slides. We don't spoon-feed tutorials. And we don't query databases just for the sake of querying. We give you realistic, messy, end-to-end assignments - then help you get better at solving them with constructive feedback.
                                    </p>
                                </div>
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-3">
                            <AccordionTrigger>Who is XP-lab for?</AccordionTrigger>
                            <AccordionContent>
                                <div className="space-y-4">
                                    <p>XP-lab is for:</p>
                                    <ul className="list-none space-y-2 ml-4">
                                        <li>🎓 Bootcamps' grads</li>
                                        <li>🎓 Universities grads</li>
                                        <li>🎓 Career switchers</li>
                                        <li>🎓 Juniors with basic skills but little confidence</li>
                                        <li>🎓 People who failed take-home assignments and want growth (and revenge)</li>
                                    </ul>
                                    <p>
                                        If you keep failing on analytical thinking or take-home assignments - you are in the right place.
                                    </p>
                                </div>
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-4">
                            <AccordionTrigger>What do I get?</AccordionTrigger>
                            <AccordionContent>
                                <div className="space-y-4">
                                    <p>In each XP-lab assignment, you'll:</p>
                                    <ul className="list-disc space-y-2 ml-6">
                                        <li>Tackle real-world, scenario-based challenges (SQL for now, but more coming soon!)</li>
                                        <li>Submit your solution in a structured workspace</li>
                                        <li>Receive feedback (AI or human)</li>
                                        <li>Reflect, improve, and track your growth across time</li>
                                    </ul>
                                </div>
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-5">
                            <AccordionTrigger>What kind of tasks will I solve?</AccordionTrigger>
                            <AccordionContent>
                                <div className="space-y-4">
                                    <p>Generally, any task that will improve your abilities to ask good questions and to be critical.</p>
                                    <p>Examples include:</p>
                                    <ul className="list-disc space-y-2 ml-6">
                                        <li>Find anomalies</li>
                                        <li>Clean and merge inconsistent tables</li>
                                        <li>Analyze A/B test results</li>
                                        <li>And much more!</li>
                                    </ul>
                                    <p>Our focus: <strong>real work, not classroom exercises</strong>.</p>
                                </div>
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-6">
                            <AccordionTrigger>Do I get feedback?</AccordionTrigger>
                            <AccordionContent>
                                <div className="space-y-4">
                                    <p>Of course! Unlike other places, this is one of the cores of XP-lab.</p>
                                    <ul className="list-disc space-y-2 ml-6">
                                        <li>Some feedback is LLM-generated</li>
                                        <li>Some is written by experienced reviewers</li>
                                        <li>Either way, it's <strong>focused on helping you improve</strong>, not just scoring you</li>
                                    </ul>
                                </div>
                            </AccordionContent>
                        </AccordionItem>

                        {/* <AccordionItem value="item-7">
                            <AccordionTrigger>What's a "cooldown"?</AccordionTrigger>
                            <AccordionContent>
                                <div className="space-y-4">
                                    <p>
                                        A <strong>cooldown</strong> is the 10-day break after you get feedback on an assignment. During that time, you can't submit another solution for this specific task—even if you want to.
                                    </p>
                                    <p>Why?</p>
                                    <p>
                                        Because rushing to "fix and resubmit" encourages shallow learning. We want you to <strong>digest the feedback</strong>, think critically, and let it sink in. Like training a muscle—you grow in the rest phase.
                                    </p>
                                </div>
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-8">
                            <AccordionTrigger>Why 10 days?</AccordionTrigger>
                            <AccordionContent>
                                <div className="space-y-4">
                                    <p>Because we're not interested in quick retries and short-term memory.</p>
                                    <p>We want you to <strong>internalize</strong> the feedback, not just patch and resubmit.</p>
                                    <p>A 10-day cooldown:</p>
                                    <ul className="list-disc space-y-2 ml-6">
                                        <li>Gives your brain time to process and reflect</li>
                                        <li>Reinforces learning through spacing (cognitive science win!)</li>
                                        <li>Promotes deep learning, not shallow hacks</li>
                                    </ul>
                                    <p>
                                        You can still explore other tasks, past feedback and prepare—but your next submission will only be available after 10 days.
                                    </p>
                                </div>
                            </AccordionContent>
                        </AccordionItem> */}


                        <AccordionItem value="item-10">
                            <AccordionTrigger>Will this help me get a job?</AccordionTrigger>
                            <AccordionContent>
                                <div className="space-y-4">
                                    <p>We don't promise jobs. We promise <strong>better experience</strong>:</p>
                                    <ul className="list-disc space-y-2 ml-6">
                                        <li>Work like a real analyst</li>
                                        <li>Communicate like a pro</li>
                                        <li>Build a portfolio of messy, practical work</li>
                                        <li>Learn to explain your decisions and improve from critique</li>
                                    </ul>
                                    <p>We help you do the job <strong>before</strong> you have the job.</p>
                                </div>
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-11">
                            <AccordionTrigger>What skills should I already have?</AccordionTrigger>
                            <AccordionContent>
                                <div className="space-y-4">
                                    <p>XP-lab is not a "learn from scratch" course. You should already have:</p>
                                    <ul className="list-disc space-y-2 ml-6">
                                        <li>The basics of SQL</li>
                                        <li>An open mind and a bag of curiosity</li>
                                    </ul>
                                    <p>We don't teach syntax. We help you <strong>use it well</strong>.</p>
                                </div>
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-12">
                            <AccordionTrigger>Can I just throw the task into ChatGPT?</AccordionTrigger>
                            <AccordionContent>
                                <div className="space-y-4">
                                    <p>Sure, you can copy and paste the whole thing. But if that's the plan, why are you here?</p>
                                    <p>We're tool-agnostic. Use ChatGPT, StackOverflow, Docs, your team chat. XP-lab exists to sharpen your judgment, not pad your prompt library.</p>
                                    <p>You level up when you:</p>
                                    <ul className="list-disc space-y-2 ml-6">
                                        <li>Ask sharper questions</li>
                                        <li>Interrogate the answers you get</li>
                                        <li>Spot hallucinations and fill in what the model misses</li>
                                        <li>Make the final call and explain why it holds water</li>
                                    </ul>
                                    <p>Go ahead and use the tools. Just make sure you own the thinking and the result.</p>
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="item-13">
                            <AccordionTrigger>I found a bug!</AccordionTrigger>
                            <AccordionContent>
                                <p>
                                    Embarrassing 😅 But since we're in Beta stage, you can help!
                                    <a
                                        href={ExternalURLs.DiscordInviteIntoBugReportChannel}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ms-1 text-sm text-muted-foreground underline hover:text-primary transition-colors"
                                    >
                                        Report a bug
                                    </a>
                                </p>
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="item-14">
                            <AccordionTrigger>Is my work private?</AccordionTrigger>
                            <AccordionContent>
                                <p>
                                    Yes. Your solutions and feedback are yours. We don't share them unless you explicitly choose to publish them in your portfolio or showcase them to others.
                                    You can read our{' '}
                                    <Link
                                        to="/privacy-policy"
                                        className="text-accent hover:text-foreground transition-colors underline underline-offset-4"
                                    >
                                        Privacy Policy
                                    </Link>
                                    .
                                </p>
                            </AccordionContent>
                        </AccordionItem>


                        <AccordionItem value="item-16">
                            <AccordionTrigger>When is it launching?</AccordionTrigger>
                            <AccordionContent>
                                <p>
                                    We're currently in private beta. If you want early access or to test XP-lab with your team, join our {" "}
                                    <button type="button" onClick={() => setIsWaitlistOpen(true)} className="text-accent hover:text-foreground transition-colors underline underline-offset-4">waitlist</button>
                                    {" "}or contact us at
                                    <a href="mailto:tal@xp-lab.com" className="text-accent hover:text-foreground transition-colors ml-1">tal@xp-lab.com</a>.
                                </p>
                                <Dialog open={isWaitlistOpen} onOpenChange={setIsWaitlistOpen}>
                                    <DialogContent className="sm:max-w-lg">
                                        <WaitingList />
                                    </DialogContent>
                                </Dialog>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>
            </div>
        </div>
    )
}
